import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { studentService } from '@/services/api'
import {
  ArrowLeft, Phone, Calendar, Building2, Users,
  CheckCircle, XCircle, TrendingUp, Clock, MonitorUp, Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { format, parseISO, startOfMonth } from 'date-fns'
import { ru } from 'date-fns/locale'
import PeriodFilter from '@/components/analytics/PeriodFilter'
import AttendanceHeatmap from '@/components/analytics/AttendanceHeatmap'
import SyncToTerminalModal from '@/components/common/SyncToTerminalModal'

const STATUS_BADGE = {
  on_time: 'badge-green',
  late: 'badge-yellow',
  very_late: 'badge-red',
  absent: 'badge-red',
  no_schedule: 'badge-gray',
}
const STATUS_LABEL = {
  on_time: 'Вовремя',
  late: 'Опоздал',
  very_late: 'Сильн. опозд.',
  absent: 'Не явился',
  no_schedule: 'Нет расписания',
}

function KPICard({ label, value, sub, color = 'text-gray-900' }) {
  return (
    <div className="card text-center py-5">
      <p className={`text-3xl font-bold ${color}`}>{value ?? 0}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  )
}

export default function StudentProfilePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const today = new Date()
  const [period, setPeriod] = useState({
    date_from: format(startOfMonth(today), 'yyyy-MM-dd'),
    date_to: format(today, 'yyyy-MM-dd'),
  })
  const [syncModalOpen, setSyncModalOpen] = useState(false)

  const { data: student, isLoading } = useQuery({
    queryKey: ['student', id],
    queryFn: () => studentService.get(id).then((r) => r.data),
  })

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['student-stats', id, period],
    queryFn: () => studentService.stats(id, period).then((r) => r.data),
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
      </div>
    )
  }

  if (!student) return <div className="text-center py-20 text-gray-400">Студент не найден</div>

  const daily = stats?.daily || []
  const chartData = daily
    .filter((d) => d.status !== 'absent')
    .map((d) => ({
      label: format(parseISO(d.date), 'dd.MM'),
      late: d.late_minutes || 0,
    }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 flex-wrap">
        <button onClick={() => navigate(-1)} className="btn-secondary btn-sm">
          <ArrowLeft size={16} /> Назад
        </button>
        <h1 className="text-2xl font-bold text-gray-900">{student.full_name}</h1>
        <span className={`badge ${student.status === 'active' ? 'badge-green' : 'badge-red'}`}>
          {student.status === 'active' ? 'Активен' : student.status}
        </span>
        <button
          className="btn-secondary btn-sm ml-auto flex items-center gap-2"
          onClick={() => setSyncModalOpen(true)}
        >
          <MonitorUp size={16} />
          На терминал
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="card">
          <div className="text-center mb-6">
            {student.photo ? (
              <img
                src={student.photo}
                className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-primary-100"
                alt={student.full_name}
              />
            ) : (
              <div className="w-32 h-32 bg-primary-100 rounded-full mx-auto flex items-center justify-center text-primary-600 text-4xl font-bold">
                {student.full_name[0]}
              </div>
            )}
            <h2 className="mt-4 text-xl font-bold text-gray-900">{student.full_name}</h2>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 text-gray-600">
              <Users size={16} className="text-gray-400 shrink-0" />
              <span>{student.class_name}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <Building2 size={16} className="text-gray-400 shrink-0" />
              
            </div>
            {student.phone && (
              <div className="flex items-center gap-3 text-gray-600">
                <Phone size={16} className="text-gray-400 shrink-0" />
                <span>{student.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-3 text-gray-600">
              <Calendar size={16} className="text-gray-400 shrink-0" />
              <span>
                {student.enrollment_date
                  ? format(new Date(student.enrollment_date), 'd MMMM yyyy', { locale: ru })
                  : '—'}
              </span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Face ID</span>
              <span className="font-mono text-xs">{student.face_id || '—'}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">На терминале</span>
              {student.is_registered_on_device ? (
                <span className="flex items-center gap-1 text-green-600 text-xs">
                  <CheckCircle size={13} /> Да
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-500 text-xs">
                  <XCircle size={13} /> Нет
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Period filter */}
          <div className="card py-3">
            <PeriodFilter onChange={setPeriod} />
          </div>

          {/* KPI Cards */}
          {statsLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Array(4).fill(0).map((_, i) => (
                <div key={i} className="card h-24 animate-pulse bg-gray-50" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KPICard
                label="Посещаемость"
                value={`${stats?.attendance_percent ?? 0}%`}
                color="text-primary-600"
              />
              <KPICard
                label="Вовремя"
                value={stats?.on_time ?? 0}
                sub={`из ${stats?.total_present ?? 0} явок`}
                color="text-green-600"
              />
              <KPICard
                label="Опозданий"
                value={(stats?.late ?? 0) + (stats?.very_late ?? 0)}
                sub={stats?.avg_late_minutes ? `ср. ${stats.avg_late_minutes} мин.` : null}
                color="text-yellow-600"
              />
              <KPICard
                label="Пропусков"
                value={stats?.absent ?? 0}
                color="text-red-600"
              />
            </div>
          )}

          {/* Info row */}
          {!statsLoading && (
            <div className="grid grid-cols-2 gap-4">
              <div className="card flex items-center gap-3 py-4">
                <Clock size={20} className="text-primary-400 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Средний приход</p>
                  <p className="text-lg font-bold text-gray-900">{stats?.avg_arrival || '—'}</p>
                </div>
              </div>
              <div className="card flex items-center gap-3 py-4">
                <TrendingUp size={20} className="text-green-400 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Всего явок за период</p>
                  <p className="text-lg font-bold text-gray-900">{stats?.total_present ?? 0}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      {!statsLoading && chartData.length > 1 && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">Динамика опозданий (мин.)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [`${v} мин.`, 'Опоздание']} />
              <Area
                type="monotone" dataKey="late" name="Опоздание"
                stroke="#f59e0b" fill="#fef3c7" strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Heatmap */}
      {!statsLoading && daily.length > 0 && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">Календарь посещений</h3>
          <AttendanceHeatmap daily={daily} dateFrom={period.date_from} dateTo={period.date_to} />
        </div>
      )}

      {/* History table */}
      {!statsLoading && daily.length > 0 && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">История посещений</h3>
          <div className="table-container max-h-96 overflow-y-auto">
            <table>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Приход</th>
                  <th>Уход</th>
                  <th>Статус</th>
                  <th>Опоздание</th>
                  <th>Отработано</th>
                </tr>
              </thead>
              <tbody>
                {[...daily].reverse().map((d) => (
                  <tr key={d.date}>
                    <td className="font-mono text-xs">{format(parseISO(d.date), 'dd.MM.yyyy')}</td>
                    <td className="font-mono text-xs text-green-700">{d.check_in || '—'}</td>
                    <td className="font-mono text-xs text-gray-500">{d.check_out || '—'}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[d.status] || 'badge-gray'} text-xs`}>
                        {STATUS_LABEL[d.status] || d.status}
                      </span>
                    </td>
                    <td className="text-gray-500 text-sm">
                      {d.late_minutes > 0 ? `${d.late_minutes} мин.` : '—'}
                    </td>
                    <td className="text-gray-500 text-sm">
                      {d.worked_minutes > 0
                        ? `${Math.floor(d.worked_minutes / 60)}ч ${d.worked_minutes % 60}м`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SyncToTerminalModal
        open={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        personType="student"
        personIds={[id]}
      />
    </div>
  )
}
